import { useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useProtocolTvl } from "@/lib/entities";
import { entityTrack } from "@/lib/entities";
import { EASE, fmtUsd, timeAgo } from "./nl";
import { AnimatedMetric } from "./AnimatedMetric";

/* TVL Intelligence — custom SVG chart to the Figma contract.
   MORPH: 320–480ms range transitions, opacity never reaches zero.
   Real provider history only; no synthetic backfill. */

const RANGES = ["7D", "30D", "90D", "1Y", "ALL"] as const;
const W = 790, H = 260, N = 90;

const resample = (pts: { t: number; tvl: number }[], n = N) => {
  if (!pts || pts.length < 2) return pts || [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const pos = (i / (n - 1)) * (pts.length - 1);
    const lo = Math.floor(pos), hi = Math.min(pts.length - 1, lo + 1);
    const f = pos - lo;
    out.push({ t: pts[lo].t + (pts[hi].t - pts[lo].t) * f,
               tvl: pts[lo].tvl + (pts[hi].tvl - pts[lo].tvl) * f });
  }
  return out;
};

export const TVLIntelligence = ({ slug, entityId }: { slug: string; entityId: string }) => {
  const reduced = useReducedMotion();
  const [range, setRange] = useState<string>("ALL");
  const { data, isLoading } = useProtocolTvl(slug, range.toLowerCase());
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const actual: { t: number; tvl: number }[] = data?.points || [];
  // resample = presentation-only geometry for path morphing. Never persisted,
  // never read back as data: tooltips/high/low/endpoints use actual observations.
  const pts = useMemo(() => resample(actual), [data]);
  const geom = useMemo(() => {
    if (pts.length < 2) return null;
    const vals = pts.map((p) => p.tvl);
    const min = Math.min(...vals), max = Math.max(...vals);
    const pad = (max - min) * 0.08 || max * 0.05;
    const y = (v: number) => H - 8 - ((v - min + pad) / (max - min + pad * 2)) * (H - 30);
    const x = (i: number) => (i / (pts.length - 1)) * W;
    const line = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.tvl).toFixed(1)}`).join("");
    const area = `${line}L${W},${H}L0,${H}Z`;
    return { line, area, x, y, min, max };
  }, [pts]);

  const setR = (r: string) => {
    setRange(r);
    setHover(null);
    entityTrack("chart_range_change", entityId, { range: r });
  };

  const onMove = (e: React.MouseEvent) => {
    if (!svgRef.current || actual.length < 2) return;
    const rect = svgRef.current.getBoundingClientRect();
    const i = Math.round(((e.clientX - rect.left) / rect.width) * (actual.length - 1));
    setHover(Math.max(0, Math.min(actual.length - 1, i)));
  };

  // Hover resolves against ACTUAL source observations (value + timestamp).
  const hoverPt = hover != null && geom && actual.length ? actual[Math.min(hover, actual.length - 1)] : null;
  const hoverX = hoverPt && actual.length > 1 ? (Math.min(hover!, actual.length - 1) / (actual.length - 1)) * W : 0;
  const fmtDate = (t: number) => new Date(t * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <div className="rounded-[22px] bg-[#06060a] border border-[#222234] p-[20px] md:p-[30px] h-full"
      style={{ boxShadow: "0 0 50px rgba(110,43,245,0.16)" }} data-testid="tvl-intelligence">
      <div className="flex items-start justify-between">
        <div>
          <AnimatedMetric value={data?.current} format={fmtUsd} baseColor="#ffffff"
            className="nl-sg block text-[28px] md:text-[34px] leading-[40px] font-medium tracking-[-0.01em]"
            testid="tvl-current" />
          <p className="nl-geist text-[9px] leading-[13px] font-semibold tracking-[0.9px] text-[#9696a8] uppercase mt-[4px]">CURRENT TVL</p>
        </div>
        <p className="nl-mono text-[9px] leading-[14px] font-medium text-[#9696a8] text-right hidden sm:block">
          SOURCE · {(data?.source === "newslayer-snapshot" ? "NEWSLAYER SNAPSHOTS" : "DEFILLAMA + NEWSLAYER SNAPSHOTS")}
          <br />{timeAgo(data?.fetched_at)}
        </p>
      </div>
      <div className="flex gap-[10px] mt-[22px]" role="tablist" aria-label="TVL range">
        {RANGES.map((r) => (
          <button key={r} role="tab" aria-selected={range === r}
            onClick={() => setR(r)}
            data-testid={`tvl-range-${r.toLowerCase()}`}
            className={`nl-geist w-[40px] md:w-[48px] h-[25px] md:h-[28px] rounded-[8px] md:rounded-[9px] text-[10px] font-semibold tracking-[0.4px] border transition-colors duration-200 ${
              range === r ? "bg-[#0e0e16] border-[#8b5cff] text-[#8b5cff]" : "border-[#222234] text-[#9696a8] hover:text-white"
            } ${r === "90D" ? "hidden sm:block" : ""}`}>{r}</button>
        ))}
      </div>
      <div className="mt-[26px] relative" onMouseLeave={() => setHover(null)}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
          className="w-full h-[200px] md:h-[260px]" onMouseMove={onMove} role="img"
          aria-label={`${range} TVL history. Current ${fmtUsd(data?.current)}, range change ${data?.change_pct ?? "—"} percent, high ${fmtUsd(data?.high?.tvl)}, low ${fmtUsd(data?.low?.tvl)}.`}>
          <defs>
            <linearGradient id="nl-tvl-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(139,92,255,0.28)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0)" />
            </linearGradient>
            <linearGradient id="nl-tvl-line" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8b5cff" />
              <stop offset="100%" stopColor="#22d3ee" />
            </linearGradient>
          </defs>
          <g opacity={0.55}>
            {[0.18, 0.4, 0.62, 0.84].map((f) => (
              <line key={f} x1={0} x2={W} y1={H * f} y2={H * f} stroke="#242437" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            ))}
          </g>
          {geom && !isLoading && (
            <>
              <motion.path animate={{ d: geom.area }} initial={false} fill="url(#nl-tvl-area)"
                transition={reduced ? { duration: 0.1 } : { duration: 0.4, ease: EASE }} />
              <motion.path animate={{ d: geom.line }} initial={false} fill="none"
                stroke="url(#nl-tvl-line)" strokeWidth={2} vectorEffect="non-scaling-stroke"
                transition={reduced ? { duration: 0.1 } : { duration: 0.4, ease: EASE }} />
              <circle cx={W} cy={geom.y(pts[pts.length - 1].tvl)} r={5} fill="#22d3ee" />
              <circle cx={W} cy={geom.y(pts[pts.length - 1].tvl)} r={11} fill="none" stroke="#22d3ee" strokeWidth={1} opacity={0.28} />
              {hoverPt && (
                <>
                  <line x1={hoverX} x2={hoverX} y1={0} y2={H}
                    stroke="#8b5cff" strokeWidth={1} opacity={0.5} vectorEffect="non-scaling-stroke" />
                  <circle cx={hoverX} cy={geom.y(hoverPt.tvl)} r={4.5} fill="#8b5cff" />
                </>
              )}
            </>
          )}
        </svg>
        {hoverPt && (
          <div className="absolute top-[6px] pointer-events-none rounded-[10px] bg-[#0a0a10] border border-[#222234] px-[12px] py-[8px]"
            style={{ left: `clamp(0px, calc(${(Math.min(hover!, actual.length - 1) / (actual.length - 1)) * 100}% - 60px), calc(100% - 130px))` }}
            data-testid="tvl-tooltip">
            <p className="nl-sg text-[15px] leading-[18px] font-medium text-white">{fmtUsd(hoverPt.tvl)}</p>
            <p className="nl-mono text-[9px] leading-[13px] font-medium text-[#9696a8] mt-[2px]">{fmtDate(hoverPt.t).toUpperCase()}</p>
          </div>
        )}
        {isLoading && (
          <div className="absolute inset-0 grid place-items-center">
            <span className="nl-mono text-[10px] tracking-[0.2em] text-[color:var(--nl-text-faint)]">RESOLVING HISTORY…</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-[28px] gap-y-[8px] mt-[16px] nl-mono text-[10px] leading-[14px] font-medium text-[#9696a8]">
        {data?.high?.tvl != null && <span>RANGE HIGH · <span className="text-white">{fmtUsd(data.high.tvl)}</span></span>}
        {data?.low?.tvl != null && <span>RANGE LOW · <span className="text-white">{fmtUsd(data.low.tvl)}</span></span>}
      </div>
    </div>
  );
};
